import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { UserDao } from "@/data/dao/service/UserDao";
import type { IUserAccessGrant } from "@/data/model/User";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { LocationAccessWindow } from "@/portal/modal/LocationAccessWindow";
import { UiNotification } from "@/portal/ui/Notification";

export class LocationAccessEditor {
    private $element: JQuery;
    private dao?: LocationDao;
    private locationId?: string;

    private search?: TableFilter;
    private dataTable?: DataTable<IUserAccessGrant>;
    private takenUserIds = new Array<string>();

    constructor(element: JQuery) {
        this.$element = element;
    }

    public loadForLocation(location: string): LocationAccessEditor {
        if (this.dao === undefined || this.dataTable === undefined) {
            this.dao = Factory.getInstance<LocationDao>(LocationDao);

            this.locationId = location;

            const tableConfig: IDataTableConfig<IUserAccessGrant> = {
                columnMapping: [
                    {
                        name: i18n("user.fields.claimType"),
                        property: "claimType",
                        sortable: false,
                        styles: { width: "40%" },
                    },
                    {
                        name: i18n("general.entities.user"),
                        property: "userId",
                        sortable: false,
                        styles: { width: "45%" },
                        formatter: (m, p) => {
                            return $('<a href="/user/' + p + '">' + Format.htmlEscape(m.userName) + "</a>").click(
                                (e) => {
                                    e.stopPropagation();
                                }
                            );
                        },
                    },
                    {
                        name: "",
                        property: "id",
                        styles: { width: "110px", "text-align": "right" },
                        sortable: false,
                        formatter: (m) => {
                            return $(
                                '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-trash"></i></a></span>'
                            ).click((e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                UiNotification.confirm(
                                    i18n("user.reallyRemoveGrant"),
                                    undefined,
                                    () => {
                                        $(e.currentTarget).replaceWith(
                                            '<div class="kt-spinner kt-spinner--sm kt-spinner--success pull-right mr-4"></div>'
                                        );
                                        this.onRevokeGrant(m);
                                    },
                                    i18n("user.removeGrant")
                                );
                            });
                        },
                    },
                ],
                autoload: false,
                paginated: false,
                useJsHistory: false,
                noDatatext: i18n("user.noGrants"),
                dataEvent: (m) => {
                    this.takenUserIds = new Array<string>();
                    for (const i in m) this.takenUserIds.push(m[i].userId);
                },
                filterProvider: {
                    getFilter: () => this.search!.getValue(),
                    setFilter: (d) => this.search!.setValue(d),
                },
            };

            this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
            this.dataTable = new DataTable<IUserAccessGrant>(
                this.$element.find("> .kt-portlet__body > .kt-datatable"),
                this.dao.aclDataSource(this.locationId),
                tableConfig
            );
            this.search.addAction(
                i18n("user.newGrant"),
                () => {
                    const window = new LocationAccessWindow(this.locationId!, this.takenUserIds);
                    window.onSaved = () => this.dataTable!.loadData();
                    window.show();
                },
                "la la-plus"
            );

            this.dataTable.listenTo(this.search);
            this.dataTable.loadData();
        }

        return this;
    }

    private onRevokeGrant(grant: IUserAccessGrant) {
        Factory.getInstance<UserDao>(UserDao)
            .removeAccess(grant.userId, grant.claimId)
            .then(() => {
                this.dataTable!.loadData();
            })
            .catch((ex) => {
                logError(ex);
                this.dataTable!.loadData();
            });
    }
}
